import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { usersApi } from '@/lib/api';

interface User {
  id: string;
  email: string;
  nombre: string;
  rol: string;
  activo: boolean;
  hogarId: string;
  hogarNombre: string;
  createdAt: string;
  updatedAt: string;
  // C12 R3 — backend always emits deletedAt (null when not soft-deleted).
  deletedAt: string | null;
}

interface UserDetail {
  id: string;
  email: string;
  nombre: string;
  rol: string;
  activo: boolean;
  permisos: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
  // C12 R3 — backend always emits deletedAt (null when not soft-deleted).
  deletedAt: string | null;
  hogar: {
    id: string;
    nombre: string;
    createdAt: string;
    miembrosCount: number;
    categoriasCount: number;
    presupuestosCount: number;
  };
}

interface Transaction {
  id: string;
  monto: number;
  descripcion: string;
  fecha: string;
  tipo: string;
  category: { nombre: string } | null;
  household: { nombre: string };
}

const rolColors: Record<string, string> = {
  OWNER: 'bg-purple-100 text-purple-800',
  ADMIN: 'bg-blue-100 text-blue-800',
  MEMBER: 'bg-gray-100 text-gray-800',
};

export default function Users() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [showSuspendModal, setShowSuspendModal] = useState(false);
  const [userToSuspend, setUserToSuspend] = useState<User | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);

  // Fetch users list
  const { data, isLoading, error } = useQuery({
    queryKey: ['users', search],
    queryFn: () => usersApi.getAll({ search: search || undefined, limit: 100 }),
  });

  // Fetch selected user details
  const { data: userDetail, isLoading: detailLoading } = useQuery({
    queryKey: ['user', selectedUserId],
    queryFn: () => usersApi.getById(selectedUserId!),
    enabled: !!selectedUserId,
  });

  // Fetch user activity
  const { data: activityData } = useQuery({
    queryKey: ['userActivity', selectedUserId],
    queryFn: () => usersApi.getActivity(selectedUserId!, 10),
    enabled: !!selectedUserId,
  });

  // Suspend mutation
  const suspendMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => usersApi.suspend(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['user', selectedUserId] });
      setShowSuspendModal(false);
      setUserToSuspend(null);
    },
  });

  // Reactivate mutation
  const reactivateMutation = useMutation({
    mutationFn: usersApi.reactivate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['user', selectedUserId] });
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: ({ id, type, reason }: { id: string; type: 'soft' | 'hard'; reason: string }) =>
      usersApi.delete(id, type, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setShowDeleteModal(false);
      setUserToDelete(null);
      setSelectedUserId(null);
    },
  });

  // Restore mutation
  const restoreMutation = useMutation({
    mutationFn: usersApi.restore,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['user', selectedUserId] });
    },
  });

  const user: UserDetail | undefined = userDetail?.data?.user;
  const activity: Transaction[] = activityData?.data?.activity || [];

  return (
    <div className="flex gap-6">
      {/* User List */}
      <div className={`${selectedUserId ? 'w-1/3' : 'w-full'}`}>
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Miembros</h1>
          <p className="text-gray-600">Vista 360 de usuarios</p>
        </div>

        {/* Search */}
        <div className="mb-4">
          <input
            type="text"
            placeholder="Buscar por email o nombre..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input w-full"
          />
        </div>

        {/* Error */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            Error al cargar usuarios: {(error as Error).message}
          </div>
        )}

        {/* User List */}
        <div className="card">
          {isLoading ? (
            <div className="p-8 text-center text-gray-500">Cargando...</div>
          ) : (
            <div className="divide-y divide-gray-100">
              {data?.data?.users?.map((u: User) => (
                <div
                  key={u.id}
                  onClick={() => setSelectedUserId(u.id)}
                  className={`p-4 cursor-pointer hover:bg-gray-50 ${
                    selectedUserId === u.id ? 'bg-primary-50 border-l-4 border-primary-500' : ''
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="font-medium text-gray-900">{u.nombre}</div>
                      <div className="text-sm text-gray-500">{u.email}</div>
                      <div className="text-xs text-gray-400 mt-1">{u.hogarNombre}</div>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${rolColors[u.rol] || 'bg-gray-100 text-gray-800'}`}>
                        {u.rol}
                      </span>
                      {u.deletedAt ? (
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-500 text-white">
                          Eliminado
                        </span>
                      ) : u.activo ? (
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Activo
                        </span>
                      ) : (
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          Suspendido
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              {data?.data?.users?.length === 0 && (
                <div className="p-8 text-center text-gray-500">No se encontraron usuarios</div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* User Detail */}
      {selectedUserId && (
        <div className="w-2/3">
          <div className="mb-4 flex justify-between items-center">
            <h2 className="text-xl font-bold text-gray-900">Detalle de Miembro</h2>
            <button
              onClick={() => setSelectedUserId(null)}
              className="text-gray-500 hover:text-gray-700"
            >
              Cerrar
            </button>
          </div>

          {detailLoading ? (
            <div className="card p-8 text-center text-gray-500">Cargando...</div>
          ) : user ? (
            <div className="space-y-6">
              {/* User Info Card */}
              <div className="card">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{user.nombre}</h3>
                    <p className="text-gray-500">{user.email}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${rolColors[user.rol] || 'bg-gray-100 text-gray-800'}`}>
                      {user.rol}
                    </span>
                    {user.deletedAt ? (
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-500 text-white">
                        Eliminado
                      </span>
                    ) : user.activo ? (
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Activo
                      </span>
                    ) : (
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        Suspendido
                      </span>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Creado:</span>{' '}
                    <span className="text-gray-900">
                      {new Date(user.createdAt).toLocaleDateString('es-MX')}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">Actualizado:</span>{' '}
                    <span className="text-gray-900">
                      {new Date(user.updatedAt).toLocaleDateString('es-MX')}
                    </span>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-gray-100 flex gap-3 flex-wrap">
                  {user.deletedAt ? (
                    /* Soft-deleted user - show restore button */
                    <button
                      onClick={() => restoreMutation.mutate(user.id)}
                      disabled={restoreMutation.isPending}
                      className="text-sm text-blue-600 hover:text-blue-700 px-3 py-1 border border-blue-200 rounded-lg hover:bg-blue-50"
                    >
                      {restoreMutation.isPending ? 'Restaurando...' : 'Restaurar Usuario'}
                    </button>
                  ) : user.activo ? (
                    /* Active user - show suspend button */
                    <button
                      onClick={() => {
                        setUserToSuspend({
                          ...user,
                          hogarId: user.hogar.id,
                          hogarNombre: user.hogar.nombre,
                        });
                        setShowSuspendModal(true);
                      }}
                      className="text-sm text-yellow-600 hover:text-yellow-700 px-3 py-1 border border-yellow-200 rounded-lg hover:bg-yellow-50"
                    >
                      Suspender
                    </button>
                  ) : (
                    /* Suspended user - show reactivate button */
                    <button
                      onClick={() => reactivateMutation.mutate(user.id)}
                      disabled={reactivateMutation.isPending}
                      className="text-sm text-green-600 hover:text-green-700 px-3 py-1 border border-green-200 rounded-lg hover:bg-green-50"
                    >
                      {reactivateMutation.isPending ? 'Reactivando...' : 'Reactivar'}
                    </button>
                  )}
                  {!user.deletedAt && (
                    <button
                      onClick={() => {
                        setUserToDelete({
                          ...user,
                          hogarId: user.hogar.id,
                          hogarNombre: user.hogar.nombre,
                        });
                        setShowDeleteModal(true);
                      }}
                      className="text-sm text-red-600 hover:text-red-700 px-3 py-1 border border-red-200 rounded-lg hover:bg-red-50"
                    >
                      Eliminar
                    </button>
                  )}
                </div>
              </div>

              {/* Hogar Info */}
              <div className="card">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Hogar: {user.hogar.nombre}
                </h3>
                <div className="grid grid-cols-3 gap-4">
                  <div className="p-3 bg-gray-50 rounded-lg text-center">
                    <div className="text-2xl font-bold text-primary-600">{user.hogar.miembrosCount}</div>
                    <div className="text-sm text-gray-500">Miembros</div>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg text-center">
                    <div className="text-2xl font-bold text-primary-600">{user.hogar.categoriasCount}</div>
                    <div className="text-sm text-gray-500">Categorias</div>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg text-center">
                    <div className="text-2xl font-bold text-primary-600">{user.hogar.presupuestosCount}</div>
                    <div className="text-sm text-gray-500">Presupuestos</div>
                  </div>
                </div>
              </div>

              {/* Recent Activity */}
              <div className="card">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Movimientos Recientes</h3>
                {activity.length === 0 ? (
                  <p className="text-gray-500">Sin movimientos recientes</p>
                ) : (
                  <div className="space-y-2">
                    {activity.map((tx) => (
                      <div key={tx.id} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0">
                        <div>
                          <div className="text-sm text-gray-900">{tx.descripcion || 'Sin descripcion'}</div>
                          <div className="text-xs text-gray-500">
                            {tx.category?.nombre || 'Sin categoria'}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className={`font-medium ${tx.tipo === 'INGRESO' ? 'text-green-600' : 'text-red-600'}`}>
                            {tx.tipo === 'INGRESO' ? '+' : '-'}${tx.monto.toFixed(2)}
                          </div>
                          <div className="text-xs text-gray-500">
                            {new Date(tx.fecha).toLocaleDateString('es-MX')}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="card p-8 text-center text-gray-500">Usuario no encontrado</div>
          )}
        </div>
      )}

      {/* Suspend Modal */}
      {showSuspendModal && userToSuspend && (
        <SuspendModal
          user={userToSuspend}
          onClose={() => {
            setShowSuspendModal(false);
            setUserToSuspend(null);
          }}
          onSubmit={(reason) => suspendMutation.mutate({ id: userToSuspend.id, reason })}
          isLoading={suspendMutation.isPending}
          error={suspendMutation.error as Error | null}
        />
      )}

      {/* Delete Modal */}
      {showDeleteModal && userToDelete && (
        <DeleteModal
          user={userToDelete}
          onClose={() => {
            setShowDeleteModal(false);
            setUserToDelete(null);
          }}
          onDelete={(type, reason) =>
            deleteMutation.mutate({ id: userToDelete.id, type, reason })
          }
          onSuspend={(reason) => {
            setShowDeleteModal(false);
            setUserToDelete(null);
            suspendMutation.mutate({ id: userToDelete.id, reason });
          }}
          isLoading={deleteMutation.isPending}
          error={deleteMutation.error as Error | null}
        />
      )}
    </div>
  );
}

function SuspendModal({
  user,
  onClose,
  onSubmit,
  isLoading,
  error,
}: {
  user: User;
  onClose: () => void;
  onSubmit: (reason: string) => void;
  isLoading: boolean;
  error: Error | null;
}) {
  const [reason, setReason] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(reason);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
        <h2 className="text-xl font-bold mb-4 text-red-600">Suspender Miembro</h2>
        <p className="text-gray-600 mb-4">
          Estas a punto de suspender a <strong>{user.nombre}</strong> ({user.email}).
          Esta accion revocara todas sus sesiones activas.
        </p>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
            {error.message}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Razon de la suspension (min 10 caracteres)
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="input w-full"
              rows={3}
              minLength={10}
              required
              placeholder="Describe la razon de la suspension..."
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button type="button" onClick={onClose} className="btn-secondary flex-1">
              Cancelar
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              disabled={isLoading || reason.length < 10}
            >
              {isLoading ? 'Suspendiendo...' : 'Suspender'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function DeleteModal({
  user,
  onClose,
  onDelete,
  onSuspend,
  isLoading,
  error,
}: {
  user: User;
  onClose: () => void;
  onDelete: (type: 'soft' | 'hard', reason: string) => void;
  onSuspend: (reason: string) => void;
  isLoading: boolean;
  error: Error | null;
}) {
  const [selectedOption, setSelectedOption] = useState<'soft' | 'hard' | 'suspend'>('soft');
  const [reason, setReason] = useState('');
  const [confirmHardDelete, setConfirmHardDelete] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedOption === 'suspend') {
      onSuspend(reason);
    } else {
      onDelete(selectedOption, reason);
    }
  };

  const isHardDeleteConfirmed = selectedOption !== 'hard' || confirmHardDelete === 'ELIMINAR';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-bold mb-2 text-red-600">Eliminar Usuario</h2>
        <p className="text-gray-600 mb-4">
          Usuario: <strong>{user.nombre}</strong> ({user.email})
        </p>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
            {error.message}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Options */}
          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Selecciona una opcion:
            </label>

            {/* Soft Delete - Recommended */}
            <label
              className={`block p-4 border-2 rounded-lg cursor-pointer transition-all ${
                selectedOption === 'soft'
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <input
                type="radio"
                name="deleteOption"
                value="soft"
                aria-label="Soft delete option"
                checked={selectedOption === 'soft'}
                onChange={() => setSelectedOption('soft')}
                className="sr-only"
              />
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 mt-0.5">
                  <span className="inline-flex items-center justify-center w-5 h-5 rounded-full border-2 border-blue-500">
                    {selectedOption === 'soft' && (
                      <span className="w-2.5 h-2.5 bg-blue-500 rounded-full" />
                    )}
                  </span>
                </div>
                <div>
                  <div className="font-medium text-gray-900 flex items-center gap-2">
                    Soft Delete
                    <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">
                      Recomendado
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    Marca el usuario como eliminado pero conserva todos sus datos.
                    Se puede restaurar en cualquier momento.
                  </p>
                </div>
              </div>
            </label>

            {/* Hard Delete */}
            <label
              className={`block p-4 border-2 rounded-lg cursor-pointer transition-all ${
                selectedOption === 'hard'
                  ? 'border-red-500 bg-red-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <input
                type="radio"
                name="deleteOption"
                value="hard"
                aria-label="Hard delete option"
                checked={selectedOption === 'hard'}
                onChange={() => setSelectedOption('hard')}
                className="sr-only"
              />
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 mt-0.5">
                  <span className="inline-flex items-center justify-center w-5 h-5 rounded-full border-2 border-red-500">
                    {selectedOption === 'hard' && (
                      <span className="w-2.5 h-2.5 bg-red-500 rounded-full" />
                    )}
                  </span>
                </div>
                <div>
                  <div className="font-medium text-gray-900 flex items-center gap-2">
                    Hard Delete + Cascade
                    <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full">
                      IRREVERSIBLE
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    Elimina PERMANENTEMENTE al usuario y TODOS sus datos asociados:
                    sesiones, alertas, aportes a metas, divisiones de gastos, etc.
                    Los movimientos se anonimizaran (se mantienen para el hogar).
                  </p>
                </div>
              </div>
            </label>

            {/* Suspend Only */}
            <label
              className={`block p-4 border-2 rounded-lg cursor-pointer transition-all ${
                selectedOption === 'suspend'
                  ? 'border-yellow-500 bg-yellow-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <input
                type="radio"
                name="deleteOption"
                value="suspend"
                aria-label="Suspend only option"
                checked={selectedOption === 'suspend'}
                onChange={() => setSelectedOption('suspend')}
                className="sr-only"
              />
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 mt-0.5">
                  <span className="inline-flex items-center justify-center w-5 h-5 rounded-full border-2 border-yellow-500">
                    {selectedOption === 'suspend' && (
                      <span className="w-2.5 h-2.5 bg-yellow-500 rounded-full" />
                    )}
                  </span>
                </div>
                <div>
                  <div className="font-medium text-gray-900">Solo Suspender</div>
                  <p className="text-sm text-gray-500 mt-1">
                    Desactiva la cuenta y revoca todas las sesiones.
                    El usuario no puede acceder pero sus datos quedan intactos.
                    Se puede reactivar facilmente.
                  </p>
                </div>
              </div>
            </label>
          </div>

          {/* Reason */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Razon (min 10 caracteres)
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="input w-full"
              rows={2}
              minLength={10}
              required
              placeholder="Describe la razon de esta accion..."
            />
          </div>

          {/* Hard Delete Confirmation */}
          {selectedOption === 'hard' && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700 mb-2">
                <strong>ATENCION:</strong> Esta accion es IRREVERSIBLE.
                Escribe <code className="bg-red-100 px-1 rounded">ELIMINAR</code> para confirmar:
              </p>
              <input
                type="text"
                value={confirmHardDelete}
                onChange={(e) => setConfirmHardDelete(e.target.value)}
                className="input w-full"
                placeholder="Escribe ELIMINAR"
              />
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button type="button" onClick={onClose} className="btn-secondary flex-1">
              Cancelar
            </button>
            <button
              type="submit"
              className={`flex-1 px-4 py-2 text-white rounded-lg disabled:opacity-50 ${
                selectedOption === 'hard'
                  ? 'bg-red-600 hover:bg-red-700'
                  : selectedOption === 'suspend'
                  ? 'bg-yellow-600 hover:bg-yellow-700'
                  : 'bg-blue-600 hover:bg-blue-700'
              }`}
              disabled={isLoading || reason.length < 10 || !isHardDeleteConfirmed}
            >
              {isLoading
                ? 'Procesando...'
                : selectedOption === 'hard'
                ? 'Eliminar Permanentemente'
                : selectedOption === 'suspend'
                ? 'Suspender'
                : 'Soft Delete'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
